// src/cockpit4/components/HermesAgentMode.tsx
// ============================================
// 代理人模式（Hermes 助手 P2-功能10）- v2.0 详细改进计划
// 授权代办 / 审批流程自动化 / 常规问题自动回复 / 重要事项升级 / 权限分级 / 审计
// 数据来源：localStorage（xeaos_hermes_agent_mode）
// ============================================

import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Bot, ShieldCheck, FileClock, AlertTriangle } from "lucide-react";

interface AgentTask {
  id: string;
  name: string;
  category: string;
  level: string;
  status: "已完成" | "处理中" | "待审批" | "已升级";
  time: string;
  result?: string;
}

const INITIAL_TASKS: AgentTask[] = [
  { id: "a1", name: "回复客户咨询：定价细节", category: "自动回复", level: "L1", status: "已完成", time: "今天 09:12", result: "已发送标准报价单模板" },
  { id: "a2", name: "审批：营销预算 5000 元", category: "审批", level: "L2", status: "待审批", time: "今天 10:30" },
  { id: "a3", name: "整理周报数据汇总", category: "数据处理", level: "L1", status: "处理中", time: "今天 11:00" },
  { id: "a4", name: "客户投诉升级处理", category: "重大事项", level: "L3", status: "已升级", time: "昨天 16:45", result: "已升级至人工，等待老板处理" },
  { id: "a5", name: "回复邮件：合同条款确认", category: "自动回复", level: "L2", status: "已完成", time: "昨天 14:20", result: "已发送条款确认稿" },
];

const PERMISSIONS = [
  { name: "常规信息回复", level: "L1 自动", enabled: true },
  { name: "文件整理与归档", level: "L1 自动", enabled: true },
  { name: "≤5000 元审批", level: "L2 授权", enabled: true },
  { name: "客户投诉处理", level: "L3 需人工", enabled: false },
  { name: "合同签署", level: "L3 需人工", enabled: false },
];

const KEY = "xeaos_hermes_agent_mode";

function load(): AgentTask[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) return JSON.parse(raw) as AgentTask[];
  } catch { /* ignore */ }
  return INITIAL_TASKS;
}

export default function HermesAgentMode() {
  const [tasks, setTasks] = useState<AgentTask[]>(load);

  const stats = useMemo(() => {
    const done = tasks.filter((t) => t.status === "已完成").length;
    const autoRate = Math.round((tasks.filter((t) => t.level === "L1").length / tasks.length) * 100);
    const upgraded = tasks.filter((t) => t.status === "已升级").length;
    return { done, autoRate, upgraded };
  }, [tasks]);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Bot size={14} className="text-cyan-400" />
        <h3 className="text-[12px] font-medium text-white">Hermes 代理人模式</h3>
        <span className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">P2-功能10 · Agent Mode</span>
        <span className="ml-auto flex items-center gap-1 text-[9px] font-mono px-2 py-1 rounded-md border border-emerald-500/30 bg-emerald-500/10 text-emerald-300">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> 代理人运行中
        </span>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <MetricCard label="今日已完成" value={`${stats.done} 项`} color="#34D399" />
        <MetricCard label="自动处理率" value={`${stats.autoRate}%`} color="#38BDF8" />
        <MetricCard label="已升级事项" value={`${stats.upgraded} 项`} color="#FBBF24" />
      </div>

      <div className="grid grid-cols-12 gap-3">
        <div className="col-span-7 rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
          <div className="flex items-center gap-2 mb-2">
            <FileClock size={11} className="text-cyan-400" />
            <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">代理任务记录</span>
          </div>
          <div className="space-y-1.5">
            {tasks.map((t) => (
              <div key={t.id} className="rounded-md border border-white/[0.04] bg-white/[0.01] p-2.5">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-medium text-white">{t.name}</span>
                  <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-white/[0.06] text-gray-400">{t.category}</span>
                  <span className="text-[8px] font-mono text-gray-600">{t.time}</span>
                  <span className={`ml-auto text-[9px] font-mono px-1.5 py-0.5 rounded ${
                    t.status === "已完成" ? "bg-emerald-500/15 text-emerald-400" : t.status === "处理中" ? "bg-sky-500/15 text-sky-400" : t.status === "待审批" ? "bg-amber-500/15 text-amber-400" : "bg-red-500/15 text-red-400"
                  }`}>{t.status}</span>
                </div>
                {t.result && <div className="text-[9px] font-mono text-gray-500 mt-1">↳ {t.result}</div>}
              </div>
            ))}
          </div>
        </div>

        <div className="col-span-5 rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
          <div className="flex items-center gap-2 mb-2">
            <ShieldCheck size={11} className="text-emerald-400" />
            <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">代理权限分级</span>
          </div>
          <div className="space-y-1">
            {PERMISSIONS.map((p) => (
              <div key={p.name} className="flex items-center gap-2 rounded-md border border-white/[0.04] bg-white/[0.01] px-2.5 py-1.5">
                <span className={`text-[9px] font-mono px-1.5 py-0.5 rounded ${p.enabled ? "bg-emerald-500/15 text-emerald-400" : "bg-gray-500/15 text-gray-500"}`}>{p.enabled ? "已授权" : "未授权"}</span>
                <span className="text-[9px] text-gray-300">{p.name}</span>
                <span className="ml-auto text-[8px] font-mono text-gray-600">{p.level}</span>
              </div>
            ))}
          </div>
          <div className="flex items-start gap-1.5 mt-2 rounded-md border border-white/[0.04] bg-white/[0.01] p-2 text-[9px] font-mono text-amber-400">
            <AlertTriangle size={10} className="mt-0.5 flex-shrink-0" />
            重要事项（L3）自动升级人工处理，所有操作留痕可审计
          </div>
        </div>
      </div>
    </div>
  );
}

function MetricCard({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-2">
      <div className="text-[9px] font-mono text-gray-500 uppercase">{label}</div>
      <div className="text-sm font-semibold font-mono mt-0.5" style={{ color }}>{value}</div>
    </motion.div>
  );
}

